/**
 * <cart-urgency-timer>
 *
 * A countdown that starts the moment the first item lands in the cart and gives
 * the shopper a fixed window in which to redeem the exclusive offers.
 *
 * What gets persisted is the deadline, not the time remaining, so a refresh, a
 * navigation or a reopened tab all resume the same countdown instead of handing
 * out a fresh window. The record is dropped once the cart empties, which is what
 * lets the next cart start a new one.
 *
 * Dawn replaces the cart markup wholesale on every cart mutation. That is fine
 * here: the replaced element disconnects and clears its interval, and the freshly
 * parsed one connects and picks the same deadline back up out of storage.
 */

const CART_URGENCY_TIMER_STORAGE_KEY = 'lecocoon:cart-urgency-timer';
const CART_URGENCY_TIMER_URGENT_AT = 5 * 60 * 1000;
// A window that closed hours ago belongs to an abandoned visit, not to someone
// refreshing their way out of the deadline. Let a genuine return visit start over
// rather than greeting it with an expired notice it can never clear.
const CART_URGENCY_TIMER_STALE_AFTER = 12 * 60 * 60 * 1000;

const CartUrgencyTimerStore = {
  read() {
    try {
      const record = JSON.parse(localStorage.getItem(CART_URGENCY_TIMER_STORAGE_KEY));
      if (!record || typeof record.deadline !== 'number' || !Number.isFinite(record.deadline)) return null;
      return record;
    } catch (error) {
      return null;
    }
  },

  write(record) {
    try {
      localStorage.setItem(CART_URGENCY_TIMER_STORAGE_KEY, JSON.stringify(record));
    } catch (error) {
      // Private browsing and exhausted quotas both land here. The countdown still
      // runs for this page view, it just cannot survive a refresh.
    }
  },

  clear() {
    try {
      localStorage.removeItem(CART_URGENCY_TIMER_STORAGE_KEY);
    } catch (error) {
      // See write().
    }
  },
};

class CartUrgencyTimer extends HTMLElement {
  connectedCallback() {
    this.valueTarget = this.querySelector('[data-timer-value]');
    this.runningTarget = this.querySelector('[data-timer-running]');
    this.expiredTarget = this.querySelector('[data-timer-expired]');
    if (!this.valueTarget) return;

    const minutes = parseInt(this.dataset.durationMinutes, 10);
    this.duration = (Number.isFinite(minutes) && minutes > 0 ? minutes : 30) * 60 * 1000;
    this.cartToken = this.dataset.cartToken || '';
    this.onExpiry = this.dataset.onExpiry || 'message';
    this.hasItems = parseInt(this.dataset.itemCount, 10) > 0;

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleStorage = this.handleStorage.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    }
    window.addEventListener('storage', this.handleStorage);
    // Browsers throttle timers in background tabs, so resync on the way back in
    // rather than trusting the tick count.
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.refresh();
  }

  disconnectedCallback() {
    this.stop();
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    window.removeEventListener('storage', this.handleStorage);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleCartUpdate(event) {
    const itemCount = event && event.cartData ? event.cartData.item_count : undefined;

    if (typeof itemCount === 'number') {
      this.hasItems = itemCount > 0;
    } else {
      // A /cart/add.js response describes the line that was added, not the whole
      // cart, so the only thing it tells us is that the cart is no longer empty.
      this.hasItems = true;
    }

    this.refresh();
  }

  handleStorage(event) {
    // Only react to another tab writing a deadline. A removal there means its
    // cart was emptied, and this tab's own cart state is the authority on that.
    if (event.key !== CART_URGENCY_TIMER_STORAGE_KEY || !event.newValue) return;
    this.refresh();
  }

  handleVisibilityChange() {
    if (!document.hidden) this.refresh();
  }

  refresh() {
    if (!this.hasItems) {
      CartUrgencyTimerStore.clear();
      this.stop();
      this.hidden = true;
      return;
    }

    this.deadline = this.resolveDeadline();
    this.hidden = false;
    this.start();
  }

  /**
   * Returns the deadline this cart is already running against, or opens a new
   * window if there isn't a usable one.
   */
  resolveDeadline() {
    const now = Date.now();
    const record = CartUrgencyTimerStore.read();

    if (record && this.belongsToCurrentCart(record) && !this.isStale(record, now)) {
      // A deadline further out than a whole window could not have been issued by
      // this element, so it was edited by hand. Replace it with an honest one.
      if (record.deadline <= now + this.duration) {
        if (this.cartToken && !record.cartToken) {
          CartUrgencyTimerStore.write(Object.assign({}, record, { cartToken: this.cartToken }));
        }
        return record.deadline;
      }
    }

    return this.openWindow(now);
  }

  isStale(record, now) {
    return now - record.deadline > CART_URGENCY_TIMER_STALE_AFTER;
  }

  belongsToCurrentCart(record) {
    // The token is blank on surfaces rendered while the cart was still empty — the
    // add-to-cart notification, mainly — so it can rule a record out but never in.
    if (!record.cartToken || !this.cartToken) return true;
    return record.cartToken === this.cartToken;
  }

  openWindow(now) {
    const deadline = now + this.duration;
    CartUrgencyTimerStore.write({
      version: 1,
      deadline: deadline,
      startedAt: now,
      cartToken: this.cartToken,
    });
    return deadline;
  }

  start() {
    this.stop();
    this.interval = setInterval(() => this.tick(), 1000);
    // Paint immediately so the element is never revealed showing a stale value.
    this.tick();
  }

  stop() {
    if (!this.interval) return;
    clearInterval(this.interval);
    this.interval = null;
  }

  tick() {
    const remaining = this.deadline - Date.now();

    if (remaining > 0) {
      this.paint(remaining);
      return;
    }

    if (this.onExpiry === 'restart') {
      this.deadline = this.openWindow(Date.now());
      this.paint(this.deadline - Date.now());
      return;
    }

    this.stop();
    this.paintExpired();
  }

  paint(remaining) {
    const totalSeconds = Math.ceil(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const formatted = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    if (this.valueTarget.textContent !== formatted) {
      this.valueTarget.textContent = formatted;
      this.valueTarget.setAttribute('datetime', `PT${minutes}M${seconds}S`);
    }

    this.classList.remove('cart-urgency-timer--expired');
    this.classList.toggle('cart-urgency-timer--urgent', remaining <= CART_URGENCY_TIMER_URGENT_AT);
    if (this.runningTarget) this.runningTarget.hidden = false;
    if (this.expiredTarget) this.expiredTarget.hidden = true;
  }

  paintExpired() {
    if (this.onExpiry === 'hide') {
      this.hidden = true;
      return;
    }

    this.classList.remove('cart-urgency-timer--urgent');
    this.classList.add('cart-urgency-timer--expired');
    if (this.runningTarget) this.runningTarget.hidden = true;
    if (this.expiredTarget) this.expiredTarget.hidden = false;
  }
}

if (!customElements.get('cart-urgency-timer')) {
  customElements.define('cart-urgency-timer', CartUrgencyTimer);
}
